import { createServerFn } from "@tanstack/react-start";
import { notFound } from "@tanstack/react-router";
import { Result } from "better-result";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { isUserAdmin } from "#/modules/admin/index.server";
import {
  getSchoolMetaData,
  getSchooljarenForCurrentSchoolAdmin,
} from "#/modules/school/api.school.server";
import { getTenantParams } from "#/modules/util.fetch-client/tenant-context.server";

/**
 * Server data for the school layout loader: school metadata (import/subscription reminders) and —
 * for admins — the browsable schooljaren. The beweegthema master-data mapping is global (not
 * school-scoped) and is loaded one level up, in the `_authenticated` layout, so both this tree and
 * the school-less admin tree share it via `useMasterData()`.
 * `isAdmin` is derived from the session here (via `isUserAdmin()`, same as root `getRouteContext`)
 * rather than trusted from client input — loaders are isomorphic, so a client-supplied `isAdmin`
 * would be attacker-controlled on client navigations.
 */
export const getSchoolLayoutData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async () => {
    if (!getTenantParams()?.school) throw notFound();

    const isAdmin = await isUserAdmin();
    const metadataResult = await getSchoolMetaData();
    if (Result.isError(metadataResult)) throw metadataResult.error;
    const schoolMetadata = metadataResult.value;

    const schooljaren = isAdmin
      ? (await getSchooljarenForCurrentSchoolAdmin())
          .map((response) => response?.schooljaren ?? [])
          .unwrapOr([])
      : [];

    return {
      schooljaren,
      schoolName: schoolMetadata.name,
      currentSchooljaar: schoolMetadata.currentSchooljaar,
      showImportReminder: schoolMetadata.showImportReminder,
      showSubscriptionReminder: schoolMetadata.showSubscriptionReminder,
      subscriptionInProgress: schoolMetadata.subscriptionInProgress,
      isZuurpruim: schoolMetadata.isZuurpruim,
    };
  });
